import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs'
import { ClientDto } from 'src/app/models/client/client-dto'
import { ClientScore } from 'src/app/models/client/client-score-dto'
import { ToolSummaryDto } from 'src/app/models/client/client-toolsummary-dto'
import { ClientUpdateDto } from 'src/app/models/client/client-update-dto'
import { FlipScores } from 'src/app/models/flip/flip-score-dto'
import { SearchParametersDto } from '../../models/Search/SearchParametersDto'
import { ApplicationInsightsService } from '../application-insights/application-insights.service'
import { ConfigService } from '../config/config.service'
import { FlipHeaderInfo } from '../../models/flip/flip-header-info'
import { FlipGraphScores } from '../../models/flip/flip-graph-scores'
import { FlipGraphSubScores } from '../../models/flip/flip-graph-sub-scores'

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private readonly clientBaseUrl =
    this.config.getSettings('webApi').baseUri + 'client/'
  private readonly clientListUrl = this.clientBaseUrl + 'list'
  private readonly clientOrgListUrl = this.clientBaseUrl + 'list/org'
  private readonly clientDetailUrl = this.clientBaseUrl + 'details'
  private readonly clientToolSummaryUrl = this.clientBaseUrl + 'toolsummaries'
  private readonly clientCreateClientUrl = this.clientBaseUrl + 'create'
  private readonly clientUpdateClientUrl = this.clientBaseUrl + 'update'
  private readonly clientSearchUrl = this.clientBaseUrl + 'list'

  private readonly getClientDetailsAndScore =
    this.clientBaseUrl + 'getclientdetailsandscore'
  private readonly pollingAmount: number =
    this.config.getSettings('webApi').creationPollingAmount

  private clientSubject = new BehaviorSubject<ClientDto>(
    null as unknown as ClientDto,
  )
  private clientIdSubject = new BehaviorSubject<string>('')

  constructor(
    private http: HttpClient,
    private appInsights: ApplicationInsightsService,
    private config: ConfigService,
  ) {}

  List(
    caseManagerContactId?: string,
    orderByColumnName?: string,
    orderBy?: string,
    maxRecords?: number,
  ): Observable<ClientDto[]> {
    const params: Record<string, any> = {}
    if (!caseManagerContactId) {
      params[caseManagerContactId] = caseManagerContactId
    }
    if (!orderByColumnName) {
      params[orderByColumnName] = orderByColumnName
    }
    if (!orderBy) {
      params[orderBy] = orderBy
    }
    if (!maxRecords) {
      params[maxRecords] = maxRecords
    }

    return this.http
      .get<ClientDto[]>(this.clientListUrl, { params: params })
      .pipe(
        map((response: any) => {
          return response as ClientDto[]
        }),
        tap((_) => this.appInsights.logEvent('Retrieved  DataVerse data')),
        catchError(async (err) => {
          //Let the top level component handle the error
          throw err
        }),
      )
  }

  SearchList(
    caseManagerContactId?: string,
    searchString?: string,
  ): Observable<ClientDto[]> {
    let searchParametersDto = new SearchParametersDto()
    searchParametersDto.PageMode = 'all-clients'
    if (caseManagerContactId) {
      searchParametersDto.CaseManagerContactId = caseManagerContactId
    }
    if (searchString) {
      searchParametersDto.SearchString = searchString
    }

    return this.http
      .post<ClientDto[]>(this.clientSearchUrl, searchParametersDto)
      .pipe(
        map((response: any) => {
          return response as ClientDto[]
        }),
        tap((_) =>
          this.appInsights.logEvent('Retrieved DataVerse search data'),
        ),
        catchError(async (err) => {
          //Let the top level component handle the error
          throw err
        }),
      )
  }

  OrgClients(
    orderByColumnName?: string,
    orderBy?: string,
    maxRecords?: number,
  ): Observable<ClientDto[]> {
    const params: Record<string, any> = {}
    if (orderByColumnName) {
      params['orderByColumnName'] = orderByColumnName
    }
    if (orderBy) {
      params['orderBy'] = orderBy
    }
    if (maxRecords) {
      params['maxRecords'] = maxRecords
    }

    return this.http.get<any>(this.clientOrgListUrl, { params: params }).pipe(
      map((response: any) => {
        return response as ClientDto[]
      }),
      tap((_) => this.appInsights.logEvent('Retrieved Org Clients')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  Details(contactId: string): Observable<ClientDto> {
    const params: Record<string, any> = { contactId: contactId }
    if (contactId === this.clientIdSubject.getValue()) {
      this.clientIdSubject.next(contactId)
      return of(this.clientSubject.value)
    }
    return this.http.get<any>(this.clientDetailUrl, { params: params }).pipe(
      map((response: any) => {
        const client = response as ClientDto
        this.clientIdSubject.next(contactId)
        this.clientSubject.next(client)
        return client
      }),
      tap((_) => this.appInsights.logEvent('Retrieved Client Details')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  DetailsAndScore(contactId: string): Observable<DetailsAndScoreData> {
    const params: Record<string, any> = { contactId: contactId }

    return this.http
      .get<{
        flipGraphDatas: any[]
        flipUpperAndLowerLimits: any[]
        flipHeaderInfo: any
        flipScores: any[]
        flipSubScores: any[]
      }>(this.getClientDetailsAndScore, { params: params })
      .pipe(
        map((response) => {
          const clientScores =
            response?.flipGraphDatas?.map(
              (item) =>
                new ClientScore({
                  ageinMonths: Number(item.ageInMonths), // Convert years to months
                  totalScoreComparision: item.scoreComparision
                    ? Number(item.scoreComparision)
                    : null, // Nullable number
                  totalScoreProfessional: Number(item.totalScoreProfessional),
                  ageString: item.ageString, // Ensure number
                }),
            ) ?? []

          const flipScores =
            response?.flipUpperAndLowerLimits?.map(
              (item) =>
                new FlipScores({
                  ageinMonths: Number(item.ageInMonths),
                  tscUpperLimit: Number(item.upperLimit), // Ensure number
                  tscLowerLimit: Number(item.lowerLimit), // Ensure number
                }),
            ) ?? []

          const flipHeaderInfo = response?.flipHeaderInfo
            ? new FlipHeaderInfo({
                lastAssessmentDate: Date.parse(
                  response.flipHeaderInfo.lastAssessmentDate,
                ),
                totalScore: Number(response.flipHeaderInfo.totalScore),
                totalCount: Number(response.flipHeaderInfo.totalCount),
              })
            : null

          const flipGraphScores =
            response?.flipScores?.map(
              (item) =>
                new FlipGraphScores({
                  orderNumber: Number(item.orderNumber),
                  groupName: item.groupName,
                  totalCount: Number(item.totalCount),
                  scoreCount: Number(item.scoreCount),
                  percentScore: Number(item.percentScore),
                  percentScoreRounded: Number(item.percentScoreRounded),
                }),
            ) ?? []

          const flipGraphSubScores =
            response?.flipSubScores?.map(
              (item) =>
                new FlipGraphSubScores({
                  name: item.name,
                  numberOfItems: Number(item.numberOfItems),
                  percentScore: Number(item.percentScore),
                  percentScoreRounded: Number(item.percentScoreRounded),
                }),
            ) ?? []

          return {
            clientScore: clientScores,
            flipScores: flipScores,
            flipHeaderInfo: flipHeaderInfo,
            flipGraphScores: flipGraphScores,
            flipGraphSubScores: flipGraphSubScores,
          } as DetailsAndScoreData
        }),
        tap((_) =>
          this.appInsights.logEvent('Retrieved Client Score and Limits Data'),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  GetToolSummaries(
    contactId: string,
    toolType: string,
  ): Observable<ToolSummaryDto[]> {
    const params: Record<string, any> = {
      contactId: contactId,
      toolType: toolType,
    }

    return this.http
      .get<any>(this.clientToolSummaryUrl, { params: params })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent('RetrievedClient Tool Summaries.'),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  CreateClient(client: ClientDto): Observable<any> {
    const params = new HttpParams().set('pollingAmount', this.pollingAmount)

    return this.http
      .post<any>(this.clientCreateClientUrl, client, { params: params })
      .pipe(
        tap((_) => this.appInsights.logEvent('Client Created.')),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  UpdateClient(client: ClientUpdateDto): Observable<any> {
    return this.http.patch<any>(this.clientUpdateClientUrl, client).pipe(
      map((response: any) => {
        this.clearClientCache()
      }),
      tap((_) => this.appInsights.logEvent('Client Updated')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  clearClientCache() {
    this.clientIdSubject.next('')
    this.clientSubject.next(null)
  }
}

export class DetailsAndScoreData {
  public clientScore: ClientScore[]
  public flipScores: FlipScores[]
  public flipHeaderInfo: FlipHeaderInfo
  public flipGraphScores: FlipGraphScores[]
  public flipGraphSubScores: FlipGraphSubScores[]
}
